export const API_PREFIX = '/api/v1';
export const DEFAULT_TIMEZONE = 'America/Tijuana';
export const ROLES = ['admin', 'user'];
export const USER_STATUSES = ['active', 'inactive', 'blocked'];
export const ROOM_STATUSES = ['active', 'inactive', 'maintenance', 'archived'];
/**
 * How a room's calendar is backed.
 *
 * - `microsoft_graph`: a Microsoft 365 **resource mailbox**. It accepts or
 *   declines on its own, and that verdict is what arbitrates conflicts, in the
 *   platform and in Outlook alike.
 * - `microsoft_invite`: a plain **user mailbox** carrying the room's address.
 *   The room is invited like a person, so the meeting lands on its calendar and
 *   its busy times are visible here — but nothing answers on its behalf, which
 *   leaves the platform as the sole arbiter. The mode exists because creating
 *   resource mailboxes needs an Exchange administrator, and this does not.
 * - `local`: no mailbox at all. The reservation lives only here.
 */
export const CALENDAR_PROVIDERS = ['microsoft_graph', 'microsoft_invite', 'local'];
export const SYNC_STATUSES = ['not_linked', 'pending', 'synced', 'error'];
export const RESERVATION_STATUSES = [
    'pending_confirmation',
    'confirmed',
    'declined',
    'cancelled',
    'completed',
];
export const RESERVATION_VISIBILITIES = ['normal', 'private'];
export const ROOM_ASSET_TYPES = ['logo', 'cover', 'gallery'];
export const ATTENDEE_RESPONSE_STATUSES = [
    'none',
    'accepted',
    'declined',
    'tentative',
];
/** Availability state used by the UI status dot. */
export const ROOM_AVAILABILITY_STATES = [
    'available',
    'soon_busy',
    'busy',
    'out_of_service',
];
/** Realtime socket event names. Keep in sync with the backend emitter. */
export const REALTIME_EVENTS = {
    reservationCreated: 'reservation.created',
    reservationUpdated: 'reservation.updated',
    reservationCancelled: 'reservation.cancelled',
    reservationDeclined: 'reservation.declined',
    roomUpdated: 'room.updated',
    roomAvailabilityChanged: 'room.availability_changed',
    notificationCreated: 'notification.created',
};
/** Default, admin-configurable booking rules (development bootstrap values). */
export const DEFAULT_BOOKING_RULES = {
    workingHoursStart: '08:00',
    workingHoursEnd: '18:00',
    bookingIntervalMinutes: 30,
    minDurationMinutes: 30,
    maxDurationMinutes: 240,
    maxAdvanceDays: 90,
};
export const UPLOAD_LIMITS = {
    logoMaxBytes: 2 * 1024 * 1024,
    coverMaxBytes: 5 * 1024 * 1024,
    galleryMaxBytes: 5 * 1024 * 1024,
    allowedMimeTypes: ['image/png', 'image/jpeg', 'image/webp'],
};
/** Typed application error codes shared by API and UI. */
export const ERROR_CODES = {
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    RESERVATION_OVERLAP: 'RESERVATION_OVERLAP',
    ROOM_UNAVAILABLE: 'ROOM_UNAVAILABLE',
    ROOM_BLOCKED: 'ROOM_BLOCKED',
    OUTSIDE_WORKING_HOURS: 'OUTSIDE_WORKING_HOURS',
    INVALID_DURATION: 'INVALID_DURATION',
    PAST_RESERVATION: 'PAST_RESERVATION',
    TOO_FAR_IN_ADVANCE: 'TOO_FAR_IN_ADVANCE',
    VERSION_CONFLICT: 'VERSION_CONFLICT',
    GRAPH_UNAVAILABLE: 'GRAPH_UNAVAILABLE',
    RESOURCE_DECLINED: 'RESOURCE_DECLINED',
    ROOM_HAS_FUTURE_RESERVATIONS: 'ROOM_HAS_FUTURE_RESERVATIONS',
    UPLOAD_INVALID: 'UPLOAD_INVALID',
    RATE_LIMITED: 'RATE_LIMITED',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
};
//# sourceMappingURL=index.js.map